/**
 * StudioGameHub `/studio/g/:id` (signed-in account) — the dedicated per-game creator
 * workspace. Reached by "Edit" on a Studio card (and the fork flow's redirect
 * from the legacy `/studio?game=<id>` deep link). Subscribes to the one
 * creation and hosts the hub TABS:
 *
 *   Overview · Iterate · Settings · Assets · Collaborators
 *
 * The active tab is mirrored in `?tab=` so a build tracker (fork/iterate) can be
 * linked directly and a refresh keeps its place. Members-only: the creation
 * subscription returns null for a non-member (RLS), which renders "not found".
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import type { Creation } from '@djgg/creations';
import { useAuth, useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useSettings } from '../hooks/useSettings.js';
import { IteratePanel } from '../studio/IteratePanel.js';
import { AssetStudio } from '../studio/AssetStudio.js';
import { CollaboratorsPanel } from '../studio/CollaboratorsPanel.js';
import { SettingsPanel } from '../studio/SettingsPanel.js';
import { OverviewPanel } from '../studio/OverviewPanel.js';
import { publishState, PUBLISH_STATE_LABEL } from '../studio/publishState.js';
import { Mascot } from '../components/Mascot.js';
import { pinnedPlayUrl } from '../services/playUrl.js';

const HUB_TABS = [
  ['overview', 'Overview'],
  ['iterate', 'Iterate'],
  ['settings', 'Settings'],
  ['assets', 'Assets'],
  ['collaborators', 'Collaborators'],
] as const;

type HubTab = (typeof HUB_TABS)[number][0];

export function StudioGameHub() {
  const services = useServices();
  const { user } = useAuth();
  const { id = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const { settings, loaded: settingsLoaded, error: settingsError, reload: reloadSettings } = useSettings();
  const [mediaReviewPilotEnabled, setMediaReviewPilotEnabled] = useState(false);
  useEffect(() => {
    let current = true;
    if (user?.role !== 'superadmin') {
      setMediaReviewPilotEnabled(false);
      return () => { current = false; };
    }
    void services.callable.getPlatformSettings({})
      .then(({ settings: snapshot, adminSettings }) => {
        if (current) setMediaReviewPilotEnabled(
          snapshot.creationFeatures.mediaReviewPilot
            && adminSettings.mediaReviewPilotCreationIds.includes(id),
        );
      })
      .catch(() => {
        if (current) setMediaReviewPilotEnabled(false);
      });
    return () => { current = false; };
  }, [id, services, user?.role]);

  // `undefined` = still loading (no emission yet); `null` = not found / no
  // access; an object = the live creation. Distinguishing the first two keeps
  // us from flashing "not found" before the first snapshot arrives.
  const creation = useSubscription<(Creation & { id: string }) | null | undefined>(
    (cb) => (id ? services.subscribe.creation(id, cb) : () => cb(null)),
    undefined,
    [id],
  );

  const rawTab = params.get('tab');
  const tab: HubTab = useMemo(
    () => (HUB_TABS.some(([t]) => t === rawTab) ? (rawTab as HubTab) : 'overview'),
    [rawTab],
  );

  function selectTab(next: HubTab) {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p, { replace: true });
  }

  if (creation === undefined) {
    return (
      <div className="container">
        <div className="empty">Loading…</div>
      </div>
    );
  }

  if (creation === null) {
    return (
      <div className="container">
        <div className="empty" data-testid="hub-not-found">
          <Mascot context="hub-not-found" size={300} />
          This game doesn’t exist or you don’t have access to it.
          <div style={{ marginTop: 12 }}>
            <Link to="/studio" className="btn btn--sm">
              ← Back to Studio
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const canPlaytest = creation.activeVersion > 0 && !!creation.activeBundleUrl;
  const showAudioHandoff = params.get('audioSetup') === '1' && creation.activeVersion > 0
    && !!user
    && (creation.creatorUid === user.uid || creation.memberUids?.includes(user.uid));
  const stagedMusic = showAudioHandoff && creation.assets?.some(asset => asset.kind === 'music');
  const stagedSfx = showAudioHandoff && creation.assets?.some(asset => asset.kind === 'sfx');
  // A failed prior version can coexist with a new build in flight. The
  // server-owned buildState is the current lifecycle authority in that case;
  // showing the historical terminal status beside "Work in progress" makes
  // the same creation contradict itself across tabs.
  const displayedStatus = creation.buildState === 'building' ? 'building' : creation.status;

  return (
    <div className="container" style={{ paddingBottom: 48 }}>
      <div className="hub__header">
        <Link to="/studio" className="hub__back" data-testid="hub-back">
          ← Studio
        </Link>
        <h1 className="hub__title">{creation.title}</h1>
        <span className="style-badge">{creation.style}</span>
        {/* Publish state (visibility), glanceable from every tab — the same
            projection the studio card chip and the Overview control use. */}
        <span
          className="pill"
          data-testid="hub-visibility"
          data-state={publishState(creation.visibility)}
        >
          {PUBLISH_STATE_LABEL[publishState(creation.visibility)]}
        </span>
        <span className={`status-dot status-dot--${displayedStatus}`} title={displayedStatus} />
        <span className="muted" style={{ fontSize: 13 }} data-testid="hub-build-status">
          {displayedStatus}
          {creation.latestVersion > 0 ? ` · v${creation.latestVersion}` : ''}
        </span>
        {canPlaytest ? (
          <a
            className="btn btn--primary btn--sm hub__preview hub__playtest"
            data-testid="hub-preview-game"
            href={pinnedPlayUrl(creation.id, creation.activeVersion)}
            target="_blank"
            rel="noopener noreferrer"
            title={`Preview active version ${creation.activeVersion} in a new tab`}
          >
            ▶ Preview live build v{creation.activeVersion} ↗
          </a>
        ) : (
          <button
            type="button"
            className="btn btn--primary btn--sm hub__preview hub__playtest"
            data-testid="hub-preview-game"
            disabled
            title="Preview becomes available after the first successful build"
          >
            ▶ Preview game
          </button>
        )}
      </div>

      {(stagedMusic || stagedSfx) && <div className="panel" data-testid="draft-audio-handoff" style={{ marginBottom: 16 }}>
        <p>Your uploaded audio is in this game’s working assets, but uploading did not bind it or make it play. Listen and choose a binding in Assets, then publish saved media.</p>
        {stagedMusic && <Link className="btn btn--sm" to={`?tab=assets&media=music&audioSetup=1`}>Choose uploaded music</Link>}
        {stagedSfx && <Link className="btn btn--sm" to={`?tab=assets&media=sound-effects&audioSetup=1`}>Choose uploaded sound effects</Link>}
      </div>}

      <div className="tabs" role="tablist" style={{ marginBottom: 18 }}>
        {HUB_TABS.map(([value, label]) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={`tab ${tab === value ? 'tab--active' : ''}`}
            onClick={() => selectTab(value)}
            data-testid={`hub-tab-${value}`}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewPanel creation={creation} />}
      {tab === 'iterate' && (!settingsLoaded
        ? <div role="status">Loading creation settings…</div>
        : settingsError
          ? <div><p role="alert">We couldn’t load the creation experience. No build was started.</p>
              <button type="button" className="btn btn--primary" onClick={reloadSettings}>Try again</button></div>
          : <IteratePanel creation={creation} creationExperience={settings.creationExperience}
              creationFilmsEnabled={settings.creationFeatures.creationFilms} />)}
      {tab === 'settings' && <SettingsPanel creation={creation} initialSection={params.get('section') ?? undefined} />}
      {tab === 'assets' && <AssetStudio
        creation={creation}
        mediaReviewPilot={mediaReviewPilotEnabled}
      />}
      {tab === 'collaborators' && <CollaboratorsPanel creation={creation} />}
    </div>
  );
}
